// Draft model for the visual strategy editor (2.8).
//
// Field edits accumulate as patch ops — the exact payload the backend applies
// as comment-preserving splices — and the UI renders the *draft* document:
// the saved tree with every pending op applied, so a field shows what it will
// become, not what it was. Nothing here parses YAML; the tree comes from the
// backend with its original keys.

import type { StrategyDocument, StrategyPatchOp } from "@/lib/types";

/** Stable identity of the field an op targets — later edits replace earlier. */
export function opKey(op: StrategyPatchOp): string {
  return `${op.scope}:${op.phase ?? ""}:${op.keyPath.join(" ")}`;
}

/**
 * Add or replace one edit. An op that writes the field's saved value back
 * (an undo by hand) still counts as an edit — cheap, predictable, and the
 * save applies it as a no-op splice.
 */
export function upsertOp(ops: StrategyPatchOp[], op: StrategyPatchOp): StrategyPatchOp[] {
  const key = opKey(op);
  const rest = ops.filter((o) => opKey(o) !== key);
  return [...rest, op];
}

export function removeOp(ops: StrategyPatchOp[], op: StrategyPatchOp): StrategyPatchOp[] {
  const key = opKey(op);
  return ops.filter((o) => opKey(o) !== key);
}

/** The draft document: saved tree + pending ops, without mutating the input. */
export function applyOpsToDocument(
  document: StrategyDocument,
  ops: StrategyPatchOp[],
): StrategyDocument {
  let draft = structuredClone(document);
  for (const op of ops) {
    draft = applyOne(draft, op);
  }
  return draft;
}

function applyOne(doc: StrategyDocument, op: StrategyPatchOp): StrategyDocument {
  const strategy = doc.strategy;
  if (!strategy) return doc;

  if (op.scope === "strategy") {
    setAtPath(strategy as Record<string, unknown>, op.keyPath, op.value);
    return doc;
  }
  const phases = strategy.phases ?? [];
  const phase = phases.find((p) => (p.id ?? p.slug) === op.phase);
  if (!phase) return doc;
  setAtPath(phase as Record<string, unknown>, op.keyPath, op.value);
  return doc;
}

function setAtPath(target: Record<string, unknown>, path: string[], value: unknown): void {
  if (path.length === 0) return;
  let cursor = target;
  for (let i = 0; i < path.length - 1; i++) {
    // In-bounds by the loop condition; hoisting satisfies noUncheckedIndexedAccess.
    const key = path[i]!;
    const next = cursor[key];
    if (typeof next !== "object" || next === null || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  const leaf = path[path.length - 1]!;
  if (value === null || value === undefined) {
    delete cursor[leaf];
  } else {
    cursor[leaf] = value;
  }
}

/** Read a value out of the (draft) document; undefined when absent. */
export function getAtPath(
  document: StrategyDocument,
  scope: "strategy" | "phase",
  phase: string | null,
  keyPath: string[],
): unknown {
  const strategy = document.strategy;
  if (!strategy) return undefined;
  let cursor: unknown =
    scope === "strategy"
      ? strategy
      : (strategy.phases ?? []).find((p) => (p.id ?? p.slug) === phase);
  for (const key of keyPath) {
    if (typeof cursor !== "object" || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}
